import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Alert, Box, Button, Typography } from '@mui/material';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import {
    approvePriceRequestTemplate,
    cancelPriceRequestTemplate,
    rejectPriceRequestTemplate,
    respondToPriceRequestTemplate,
    savePriceRequestLinesTemplate,
    sendPriceRequestTemplate,
} from 'apollo/templates/price-request';
import { PriceRequest, PriceRequestAction, PriceRequestSide, PriceRequestStatus } from 'models/price-request';
import { errorMessageKey, hasAction, isPriceRequestChanged } from './price-request-format';
import PriceRequestSubmitCard from './price-request-submit-card.component';
import { PriceRequestResponse } from './use-price-request-response';

type PriceRequestActionsProps = {
    request: PriceRequest;
    /** The lab's unsaved answers; present only when the request allows EDIT_PRICES. */
    response?: PriceRequestResponse;
    /** Re-read the request (after the lab changed it under an approval). */
    onReload: () => Promise<unknown>;
};

type Confirmation = 'approve' | 'reject' | 'cancel';

/**
 * DD-3697: the request's buttons — each one only when `allowedActions` holds it. Every
 * mutation returns the whole request, which Apollo writes back into the cache by _id.
 */
const PriceRequestActions: FC<PriceRequestActionsProps> = ({ request, response, onReload }) => {
    const { t: translate } = useTranslation('price-requests');
    const [confirmation, setConfirmation] = useState<Confirmation>();
    const [feedback, setFeedback] = useState<{ severity: 'error' | 'warning' | 'success'; message: string }>();

    const onError = (error: unknown) => {
        setConfirmation(undefined);
        setFeedback({ severity: 'error', message: translate(errorMessageKey(error)) });
    };
    // The log is not mounted here; it reloads on its next visit (cache-and-network).
    const options = { onError };
    const [saveLines, saveState] = useMutation(savePriceRequestLinesTemplate, { onError });
    const [send, sendState] = useMutation(sendPriceRequestTemplate, options);
    const [respond, respondState] = useMutation(respondToPriceRequestTemplate, options);
    const [approve, approveState] = useMutation(approvePriceRequestTemplate, {
        onError: async (error) => {
            if (!isPriceRequestChanged(error)) return onError(error);
            setConfirmation(undefined);
            await onReload();
            setFeedback({ severity: 'warning', message: translate('PRICES_CHANGED_RELOADED') });
        },
    });
    const [reject, rejectState] = useMutation(rejectPriceRequestTemplate, options);
    const [cancel, cancelState] = useMutation(cancelPriceRequestTemplate, options);

    const isBusy = [saveState, sendState, respondState, approveState, rejectState, cancelState].some(
        (state) => state.loading,
    );
    const variables = { id: request._id };
    const can = (action: PriceRequestAction) => hasAction(request, action);
    const succeeded = (message: string) => () => setFeedback({ severity: 'success', message: translate(message) });

    const saveAnswers = async () => {
        if (!response) return true;
        setFeedback(undefined);
        const result = await saveLines({ variables: { ...variables, lines: response.toInput() } });
        return Boolean(result.data);
    };

    const handleSave = async () => {
        if (await saveAnswers()) succeeded('SAVED')();
    };

    const handleSend = async () => {
        // A lab-opened draft carries the lab's prices: save them, then send.
        if (can(PriceRequestAction.EDIT_PRICES) && !(await saveAnswers())) return;
        setFeedback(undefined);
        const result = await send({ variables });
        if (result.data) succeeded('SENT_SUCCESS')();
    };

    const handleRespond = async () => {
        setFeedback(undefined);
        const result = await respond({ variables: { ...variables, lines: response?.toInput() } });
        if (result.data) succeeded('RESPONDED_SUCCESS')();
    };

    const confirm = async () => {
        setFeedback(undefined);
        const run = {
            approve: () => approve({ variables: { ...variables, revision: request.revision } }),
            reject: () => reject({ variables }),
            cancel: () => cancel({ variables }),
        }[confirmation as Confirmation];
        const result = await run();
        setConfirmation(undefined);
        if (result.data) succeeded(`${(confirmation as Confirmation).toUpperCase()}_SUCCESS`)();
    };

    const summary = response?.counts ?? request.summary;
    // Sending is the last step and lives in its own card below; Save stays with the table.
    const isOnlySave =
        can(PriceRequestAction.EDIT_PRICES) && !can(PriceRequestAction.SEND) && !can(PriceRequestAction.RESPOND);
    const hasAnyButton = [PriceRequestAction.EDIT_PRICES, PriceRequestAction.APPROVE, PriceRequestAction.REJECT].some(
        can,
    );

    const names = { chain: request.chain.name ?? '', lab: request.lab.name ?? '' };
    // Sending is only offered once the table is saved, so the lab sends exactly what it saved.
    const hasUnsavedChanges = Boolean(response?.isDirty);
    // An offered product without a price or delivery time cannot be saved or sent.
    // Named, not just marked: the delivery time column may be scrolled out of view.
    const missingLines = response?.missingLines ?? [];
    const hasMissingValues = missingLines.length > 0;
    const missingNote = hasMissingValues ? (
        <Box mt={1} color="error.main" data-test="price-request.missing-note">
            {translate('REQUIRED_VALUES_MISSING', {
                count: missingLines.length,
                products: missingLines.map((line) => line.actionNumber || line.actionName).join(', '),
            })}
        </Box>
    ) : undefined;
    const unsavedNote = hasUnsavedChanges ? (
        <Box mt={1} color="warning.dark" data-test="price-request.unsaved-note">
            {translate('SAVE_BEFORE_SENDING')}
        </Box>
    ) : undefined;

    const renderSubmitCard = () => {
        if (can(PriceRequestAction.RESPOND)) {
            return (
                <PriceRequestSubmitCard
                    title={translate('SUBMIT_RESPONSE_TITLE', names)}
                    submit={{
                        label: translate('SEND_RESPONSE'),
                        onClick: handleRespond,
                        disabled: isBusy || hasUnsavedChanges || hasMissingValues,
                        dataTest: 'price-request.respond',
                    }}
                >
                    {translate('SUBMIT_RESPONSE_TEXT', names)} {translate('SUBMIT_RESPONSE_NOTE', names)}
                    {unsavedNote}
                </PriceRequestSubmitCard>
            );
        }
        if (can(PriceRequestAction.SEND)) {
            const isLab = request.viewerSide === PriceRequestSide.LAB;
            return (
                <PriceRequestSubmitCard
                    title={translate(isLab ? 'SUBMIT_RESPONSE_TITLE' : 'SUBMIT_REQUEST_TITLE', names)}
                    submit={{
                        label: translate(isLab ? 'SEND_RESPONSE' : 'SEND'),
                        onClick: handleSend,
                        disabled: isBusy || hasUnsavedChanges || hasMissingValues,
                        dataTest: 'price-request.send',
                    }}
                >
                    {translate(isLab ? 'SUBMIT_RESPONSE_TEXT' : 'SUBMIT_REQUEST_TEXT', names)}
                    {isLab ? ` ${translate('SUBMIT_RESPONSE_NOTE', names)}` : ''}
                    {unsavedNote}
                </PriceRequestSubmitCard>
            );
        }
        if (request.viewerSide === PriceRequestSide.LAB && request.status === PriceRequestStatus.RESPONDED) {
            return (
                <PriceRequestSubmitCard title={translate('SUBMITTED_RESPONSE_TITLE', names)}>
                    {translate('SUBMITTED_RESPONSE_TEXT', names)}
                </PriceRequestSubmitCard>
            );
        }
        return undefined;
    };

    const renderCancelCard = () => {
        if (!can(PriceRequestAction.CANCEL)) return undefined;
        const isDraft = request.status === PriceRequestStatus.DRAFT;
        const counterpart = request.viewerSide === PriceRequestSide.LAB ? names.chain : names.lab;
        return (
            <PriceRequestSubmitCard
                tone="error"
                title={translate(isDraft ? 'DISCARD_DRAFT_TITLE' : 'CANCEL_REQUEST_TITLE')}
                submit={{
                    label: translate(isDraft ? 'DISCARD_DRAFT' : 'CANCEL_REQUEST'),
                    onClick: () => setConfirmation('cancel'),
                    disabled: isBusy,
                    dataTest: 'price-request.cancel',
                }}
            >
                {translate(isDraft ? 'DISCARD_DRAFT_TEXT' : 'CANCEL_REQUEST_TEXT', { counterpart })}
            </PriceRequestSubmitCard>
        );
    };

    return (
        <Box mt={2}>
            {feedback ? (
                <Alert
                    severity={feedback.severity}
                    sx={{ mb: 2 }}
                    onClose={() => setFeedback(undefined)}
                    data-test="price-request.feedback"
                >
                    {feedback.message}
                </Alert>
            ) : undefined}
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="body2" color="text.secondary" data-test="price-request.summary">
                        {translate('SUMMARY_TEXT', summary)}
                    </Typography>
                    {missingNote}
                </Box>
                {hasAnyButton ? (
                    <Box display="flex" gap={1} flexWrap="wrap">
                        {can(PriceRequestAction.REJECT) ? (
                            <Button
                                variant="contained"
                                color="error"
                                disabled={isBusy}
                                onClick={() => setConfirmation('reject')}
                                data-test="price-request.reject"
                            >
                                {translate('REJECT')}
                            </Button>
                        ) : undefined}
                        {can(PriceRequestAction.EDIT_PRICES) ? (
                            <Button
                                variant={isOnlySave ? 'contained' : 'text'}
                                disabled={isBusy || hasMissingValues || (isOnlySave && !response?.isDirty)}
                                onClick={handleSave}
                                data-test="price-request.save"
                            >
                                {translate(request.status === PriceRequestStatus.DRAFT ? 'SAVE_DRAFT' : 'SAVE_CHANGES')}
                            </Button>
                        ) : undefined}
                        {can(PriceRequestAction.APPROVE) ? (
                            <Button
                                variant="contained"
                                disabled={isBusy}
                                onClick={() => setConfirmation('approve')}
                                data-test="price-request.approve"
                            >
                                {translate('APPROVE')}
                            </Button>
                        ) : undefined}
                    </Box>
                ) : undefined}
            </Box>
            {renderSubmitCard() || renderCancelCard() ? (
                // One full-width card per step, the same on both sides: send first, cancel below.
                <Box mt={3} display="flex" flexDirection="column" gap={2}>
                    {renderSubmitCard()}
                    {renderCancelCard()}
                </Box>
            ) : undefined}
            <ConfirmDialog
                open={Boolean(confirmation)}
                handleClose={() => setConfirmation(undefined)}
                handleConfirm={confirm}
                dialogTitle={confirmation ? translate(`CONFIRM_${confirmation.toUpperCase()}_TITLE`) : undefined}
                dialogContent={
                    confirmation ? (
                        <Typography>
                            {translate(`CONFIRM_${confirmation.toUpperCase()}_TEXT`, {
                                ...request.summary,
                                number: request.number,
                            })}
                        </Typography>
                    ) : undefined
                }
                confirmBtnLabel={confirmation ? translate(`CONFIRM_${confirmation.toUpperCase()}_BUTTON`) : undefined}
                declineBtnLabel={translate('BACK')}
            />
        </Box>
    );
};

export default PriceRequestActions;
